import type { GpsValidation } from '@fieldvision/contracts';

/**
 * The four FR-03 verdicts, in words a manager reads rather than enum members.
 *
 * ONE TABLE, THREE CALLERS — the reports list, the overview's open-visits panel
 * and the visit-detail dialog. It lives here because it was previously written
 * out three times as `=== 'OUT_OF_TOLERANCE' ? warn : success('Valid')`, and
 * that shape has a defect the moment there are more than two verdicts: it is a
 * FALLBACK, so anything unrecognised renders as a green "Valid". `UNAVAILABLE`
 * has been rendering as "Valid" in two of those three places for as long as they
 * have existed — the system saying a check passed when it explicitly could not
 * run. Adding a fourth verdict would have doubled that.
 *
 * `Record<GpsValidation, …>` rather than a lookup with a default, so the next
 * value added to the enum is a compile error in every consumer at once instead
 * of a wrong badge in the two nobody remembered to update.
 */
export const GPS_LABEL: Record<GpsValidation, string> = {
  IN_TOLERANCE: 'Valid',
  OUT_OF_TOLERANCE: 'Di luar toleransi',
  /* The handset. Nothing anyone can do about it after the fact. */
  UNAVAILABLE: 'Tidak ada sinyal',
  /*
   * The master data, and phrased as an instruction because unlike the other
   * three this one is fixable: somebody can go and survey the shop, and then
   * every future visit there is checked properly.
   */
  NOT_CONFIGURED: 'Koordinat Outlet belum diatur',
};

/**
 * NEITHER "NO VERDICT" STATE IS GREEN, and neither is red.
 *
 * `neutral` is the whole point: a supervisor scanning this column must be able
 * to see at a glance that the check did not run, without it reading either as a
 * pass (which would let an unchecked visit count as verified) or as an
 * accusation (which would put a salesperson in the fraud queue over a coordinate
 * the office never entered).
 */
export const GPS_TONE: Record<GpsValidation, 'success' | 'warn' | 'neutral'> = {
  IN_TOLERANCE: 'success',
  OUT_OF_TOLERANCE: 'warn',
  UNAVAILABLE: 'neutral',
  NOT_CONFIGURED: 'neutral',
};
